import React, {useEffect, useRef, useState} from 'react'
import {SettingPageTitle} from '../components/header/SettingPageTitle'
import {KTCard, toAbsoluteUrl} from '../../../../_metronic/helpers'
import ProductDrawer from '../components/drawer/ProductDrawer'
import {getDownloadURL, ref, uploadBytes} from 'firebase/storage'
import db, {storage} from '../../../../services/firebase'
import {
  DocumentReference,
  addDoc,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore'
import {useAuth} from '../../../modules/auth'
import {ToastContainer} from 'react-toastify'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import Button from 'app/components/Button'
import {ListLoading} from 'app/components/ListLoading'
import { useSearchParams } from 'react-router-dom'

const SettingsCompany = () => {
  const blankImg = toAbsoluteUrl('/media/svg/avatars/blank.svg')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [image, setImage] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(blankImg)
  const fileInputRef = useRef(null)
  const [displayName, setDisplayName] = useState('')
  const [legalName, setLegalName] = useState('')
  const [address, setAddress] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const {currentUser} = useAuth()
  const [productLength, setProductLength] = useState(0)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const focusRef = useRef<HTMLInputElement | null>(null)
  const [isDataFetching, setIsDataFetching] = useState(true)
  const [searchParams] = useSearchParams();

  useEffect(() => {
    setTimeout(() => {
      if (focusRef.current) {
        focusRef.current.focus()
      }
    }, 300)
  }, [])

  useEffect(() => {
    if (searchParams.get('openDrawer') === 'true') {
      setIsModalOpen(true);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchCompanyData = async () => {
      if (currentUser?.tenantId) {
        setIsDataFetching(true)
        const tenantRef = doc(db, 'tenants', currentUser.tenantId)
        const companyRef = doc(tenantRef, 'settings', 'company')
        const docSnap = await getDoc(companyRef)

        if (docSnap.exists()) {
          const companyData = docSnap.data()
          setDisplayName(companyData.displayName || '')
          setLegalName(companyData.legalName || '')
          setAddress(companyData.address || '')
          if (companyData.logoUrl) {
            setPreviewUrl(companyData.logoUrl)
          }
        }
        setTimeout(() => setIsDataFetching(false), 500)
      }
    }

    fetchCompanyData()
  }, [currentUser])

  useEffect(() => {
    const fetchCompanyData = async () => {
      if (currentUser?.tenantId) {
        const tenantRef = doc(db, 'tenants', currentUser?.tenantId)
        const companyRef = doc(tenantRef, 'settings', 'product')
        const docSnap = await getDoc(companyRef)

        if (docSnap.exists() && docSnap.data().newAsset) {
          // Check if product data exists and get its length
          const productDataLength = Object.keys(docSnap.data().newAsset).length
          setProductLength(productDataLength)
        } else {
          setProductLength(0)
        }
      }
    }

    fetchCompanyData()
  }, [currentUser])

  const handleOpenModal = () => {
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const handleImageChange = (event) => {
    const file = event.target.files[0]
    if (
      file &&
      (file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg')
    ) {
      setImage(file)
      setPreviewUrl(URL.createObjectURL(file))
    } else {
      alert('Please select an image file (png, jpg, or jpeg)')
    }
  }

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      ;(fileInputRef.current as HTMLInputElement).click()
    }
  }

  const handleDelete = () => {
    setImage(null)
    setPreviewUrl(blankImg)
    if (fileInputRef.current) {
      ;(fileInputRef.current as HTMLInputElement).value = ''
    }
    // Here you might want to delete the image from the server if it was uploaded
    // deleteUserProfileImage();
  }

  const validateForm = () => {
    const errors: Record<string, string> = {}

    if (!displayName.trim()) {
      errors.displayName = 'Display name is required'
    }

    if (!legalName.trim()) {
      errors.legalName = 'Legal name is required'
    }

    if (!address.trim()) {
      errors.address = 'Address is required'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!validateForm()) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)

    try {
      let imageUrl = previewUrl

      if (image) {
        const storageRef = ref(
          storage,
          `tenants/${currentUser?.tenantId}/settings/company_logos/${(image as File).name}`
        )
        await uploadBytes(storageRef, image)
        imageUrl = await getDownloadURL(storageRef)
      }

      const newAsset = {
        displayName,
        legalName,
        address,
        logoUrl: imageUrl,
        updatedAt: new Date().toISOString(),
      }

      if (!currentUser?.tenantId) {
        throw new Error('No tenant ID found')
      }
      const tenantId = currentUser.tenantId

      const tenantRef = doc(db, 'tenants', tenantId)
      const companyRef: DocumentReference = doc(tenantRef, 'settings', 'company')
      const trustCenterHeaderDetailsRef = doc(
        db,
        'tenants',
        tenantId,
        'trust_center',
        'headerDetails'
      )

      await setDoc(companyRef, newAsset)
      await setDoc(
        trustCenterHeaderDetailsRef,
        {
          companyLogoURL: imageUrl,
          companyName: displayName,
        },
        {
          merge: true,
        }
      )

      const currentDate = new Date()
      const year = currentDate.getFullYear()
      const month = currentDate.getMonth() + 1
      const day = currentDate.getDate()
      const todaysDate = `${month}/${day}/${year}`

      // Add activity log
      const activitiesRef = collection(tenantRef, 'activities')
      await addDoc(activitiesRef, {
        message: `Company Details has been updated`,
        timestamp: serverTimestamp(),
        tenantId: currentUser.tenantId,
        uid: currentUser.uid,
        username: currentUser.userName,
        date: todaysDate,
        email: currentUser.email,
        target: 'Settings',
        action: 'Company details updated',
      })
      showSuccessToast({message: 'Company details updated successfully'})
    } catch (error) {
      showErrorToast({
        message: 'An error occurred while updating company details. Please try again.',
      })
    } finally {
      setIsLoading(false)
    }
  }
  return (
    <>
      <SettingPageTitle
        icon='Settings.svg'
        title='Settings'
        desc=' Manage your settings'
        // breadcrumbs={usersBreadcrumbs}
      >
        Personnel Management
      </SettingPageTitle>

      {isDataFetching ? (
        <KTCard>
          <div className='position-relative' style={{height: '500px'}}>
            <ListLoading />
          </div>
        </KTCard>
      ) : (
        <>
          <form onSubmit={handleSubmit}>
            <div className='card'>
              <div className='card-header px-6 pt-4'>
                <h4 className='fw-bold m-4'>Company Details</h4>
              </div>
              <div className='card-body'>
                <div className='row align-items-center  mb-4'>
                  <div className='col-md-2'>
                    <div
                      className='image-input image-input-empty '
                      data-kt-image-input='true'
                      style={{
                        backgroundImage: `url('${previewUrl}')`,
                        backgroundPosition: 'center',
                      }}
                    >
                      <div className='image-input-wrapper w-125px h-125px'></div>
                      <input
                        type='file'
                        name='avatar'
                        ref={fileInputRef}
                        hidden
                        accept='.png, .jpg, .jpeg'
                        onChange={handleImageChange}
                      />
                      <input type='hidden' name='avatar_remove' />
                      {/* </label> */}
                      <span
                        className='btn btn-icon btn-circle btn-color-muted btn-active-color-primary w-25px h-25px bg-body shadow'
                        data-kt-image-input-action='cancel'
                        data-bs-toggle='tooltip'
                        title='Cancel avatar'
                      >
                        <i className='ki-outline ki-cross fs-3'></i>
                      </span>
                      <span
                        className='btn btn-icon btn-circle btn-color-muted btn-active-color-primary w-25px h-25px bg-body shadow'
                        data-kt-image-input-action='remove'
                        data-bs-toggle='tooltip'
                        title='Remove avatar'
                      >
                        <i className='ki-outline ki-cross fs-3'></i>
                      </span>
                    </div>
                  </div>
                  <div className='col-md-9'>
                    <div className='mb-2 '>
                      <div className='fw-bold fs-6 fw-bliock'>Company logo</div>
                      <div className='fs-6'>Allowed file types: png, jpg and jpeg.</div>
                    </div>
                    <div className='d-flex mt-3' style={{gap: '12px'}}>
                      {previewUrl === blankImg && (
                        <Button type='submit' btnType='primary' onClick={handleUploadClick}>
                          Upload New Picture
                        </Button>
                      )}
                      {previewUrl !== blankImg && (
                        <Button type='submit' onClick={handleDelete}>
                          Delete Picture
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <div className='row align-items-center mb-4 mt-4'>
                  <div className='col-md-12'>
                    <div className='fv-row mb-3'>
                      <label className='fw-bold fs-6 mb-1 required'>Display Name</label>
                      <input
                        placeholder='Enter display name'
                        type='text'
                        className={`form-control ${formErrors.displayName ? 'is-invalid' : ''}`}
                        name='displayName'
                        value={displayName}
                        onChange={(e) => {
                          setDisplayName(e.target.value)
                          setFormErrors((prev) => ({...prev, displayName: ''}))
                        }}
                        autoComplete='off'
                        ref={focusRef}
                      />
                      {formErrors.displayName && (
                        <span className='text-danger'>{formErrors.displayName}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className='row align-items-center mb-4 mt-4'>
                  <div className='col-md-12'>
                    <div className='fv-row mb-3'>
                      <label className='fw-bold fs-6 mb-1 required'>Legal Name</label>
                      <input
                        placeholder='Enter legal name'
                        type='text'
                        className={`form-control ${formErrors.legalName ? 'is-invalid' : ''}`}
                        name='legalName'
                        value={legalName}
                        onChange={(e) => {
                          setLegalName(e.target.value)
                          setFormErrors((prev) => ({...prev, legalName: ''}))
                        }}
                        autoComplete='off'
                      />
                      {formErrors.legalName && (
                        <span className='text-danger'>{formErrors.legalName}</span>
                      )}
                      <div className='fs-7' style={{color: '#828282'}}>
                        Will be used solely for audit purposes
                      </div>
                    </div>
                  </div>
                </div>

                <div className='row align-items-center mt-4'>
                  <div className='col-md-12'>
                    <div className='fv-row mb-3'>
                      <label className='fw-bold fs-6 mb-1 required'>Address</label>
                      <input
                        placeholder='Enter address'
                        type='text'
                        className={`form-control ${formErrors.address ? 'is-invalid' : ''}`}
                        name='address'
                        value={address}
                        onChange={(e) => {
                          setAddress(e.target.value)
                          setFormErrors((prev) => ({...prev, address: ''}))
                        }}
                        autoComplete='off'
                      />
                      {formErrors.address && (
                        <span className='text-danger'>{formErrors.address}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className='card-footer py-0'>
                <div className='d-flex justify-content-end'>
                  <Button type='submit' btnType='primary' className='my-6' disabled={isLoading}>
                    {isLoading ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            </div>
          </form>
          {currentUser?.account_type !== 'trust' && (
            <>
              <div className='card mt-8'>
                <div className='card-header px-5 pt-4'>
                  <h4 className='fw-bold m-4'>Product</h4>
                </div>

                <div className='card-body p-0 mx-8'>
                  <div className='fs-6  fw-semibold my-4 ' style={{color: '#5A5A5A'}}>
                    Please make sure to add the product that will be in scope of your audit.
                  </div>
                  <Button btnType='primary' className='mb-8' onClick={handleOpenModal}>
                    {productLength > 0 ? 'View Product Details' : 'Add Product'}
                  </Button>
                </div>
              </div>
              <ProductDrawer isOpen={isModalOpen} onClose={handleCloseModal} />
            </>
          )}
        </>
      )}

      <ToastContainer />
    </>
  )
}

export default SettingsCompany
